/* Staten Tools engraving preview: black single-color layout preview (not an artwork editor).
   Global API: window.stEngravingPreview.init(root), .export(root) -> {file, settings, size}.
   PDF.js is pinned and lazy-loaded from jsDelivr only when a PDF is chosen. */
(() => {
  'use strict';
  if (window.stEngravingPreview) return;
  const PDFJS_VERSION = '4.10.38';
  const CDN = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${PDFJS_VERSION}/build`;
  const DPI = 300;
  const SHEAVES = {'6': [4, 0.5], '12': [8, 1], '18': [8, 1.5], '24': [8, 1.5]};
  // Feeding sizes use a flat artwork layout, not a claimed physical frame size.
  const FRAME_FLAT = [4, 2];
  SHEAVES['feed-3'] = FRAME_FLAT;
  for (const size of ['4', '5', '6']) SHEAVES[`feed-${size}`] = [8, 2.5];
  const FONTS = {
    sans: '700 {s}px "Helvetica Neue", Arial, sans-serif',
    serif: '700 {s}px Georgia, "Times New Roman", serif',
    mono: '700 {s}px "Courier New", Courier, monospace'
  };
  const states = new WeakMap();
  const q = (root, s) => root.querySelector(s);
  let pdfjsPromise = null;

  function loadPdfjs() {
    if (pdfjsPromise) return pdfjsPromise;
    pdfjsPromise = (async () => {
      let lib;
      try { lib = await import(`${CDN}/pdf.min.mjs`); }
      catch (e) { throw Error('The PDF preview library could not be loaded. Check your connection or content blockers and try again.'); }
      if (!lib?.getDocument || lib.version !== PDFJS_VERSION) throw Error('The PDF preview library version is unexpected. Refresh and try again.');
      // Cross-origin module workers cannot be constructed directly; wrap in a same-origin blob.
      let worker;
      try {
        const url = URL.createObjectURL(new Blob([`import "${CDN}/pdf.worker.min.mjs";`], {type: 'text/javascript'}));
        worker = new Worker(url, {type: 'module'});
      } catch (e) { throw Error('Your browser blocked the PDF preview worker. Try a current version of Chrome, Safari, Edge or Firefox.'); }
      lib.GlobalWorkerOptions.workerPort = worker;
      return lib;
    })();
    pdfjsPromise.catch(() => { pdfjsPromise = null; });
    return pdfjsPromise;
  }

  const kind = root => window.stEngravingPackages?.selected(root)?.kind || q(root, '[data-eng-option]')?.selectedOptions[0]?.dataset.kind || '';
  const num = (el, d) => { const v = Number(el?.value); return Number.isFinite(v) ? v : d; };
  function profile(root) {
    const selected = q(root, '[data-eng-sheave]')?.value ?? root.dataset.sheaveSize ?? '';
    const size = Object.hasOwn(SHEAVES, selected) ? selected : '';
    // Frame-only packages (hook or feeding) use the flat frame artwork layout, never wheel bounds.
    const code = window.stEngravingPackages?.selected(root)?.code;
    const [maxW, maxH] = size.startsWith('feed-') ? SHEAVES[size]
      : code === 'FRAME' ? FRAME_FLAT : SHEAVES[size] || FRAME_FLAT;
    return {size, maxW, maxH, frameOnly: code === 'FRAME'};
  }
  function syncLimits(root, reset = false) {
    window.stEngravingLocations?.sync(root);
    const {size, maxW, maxH, frameOnly} = profile(root);
    const mode = `${size}:${frameOnly ? 'frame' : 'wheel'}`;
    if (root.dataset.engLimitMode !== mode) { if (root.dataset.engLimitMode) reset = true; root.dataset.engLimitMode = mode; }
    for (const [selector, max] of [['[data-eng-w]', maxW], ['[data-eng-h]', maxH]]) {
      const el = q(root, selector);
      el.max = String(max);
      if (reset || Number(el.value) > max) el.value = String(max);
    }
    const label = q(root, '[data-eng-limit]');
    const locations = window.stEngravingLocations?.profile(root);
    if (label) label.textContent = locations?.frameSides && !locations.wheelSides
      ? (['feed-4', 'feed-5', 'feed-6'].includes(size)
        ? `Maximum ${maxW}″ wide × ${maxH}″ high on this ${size.slice(5)}″ feeding-sheave frame.`
        : 'Flat artwork layout only. Artwork is fitted to the frame; final engraving dimensions are confirmed by our team.')
      : `Maximum ${maxW}″ long × ${maxH}″ high per wheel mark${size ? ` on this ${size}″ sheave` : ''}.${locations?.marksPerFace === 3 ? ' Each of the three marks per face uses this size.' : ''}${locations?.frameSides ? ' Frame artwork is fitted separately to the arm; wheel dimensions do not apply to the frame.' : ''}`;
  }

  function dims(root) {
    const {maxW, maxH} = profile(root);
    const w = num(q(root, '[data-eng-w]'), maxW), h = num(q(root, '[data-eng-h]'), maxH);
    const ok = w >= 0.1 && w <= maxW && h >= 0.1 && h <= maxH;
    return {w, h, ok};
  }

  function setStatus(root, st, status, text) {
    st.status = status;
    if (status === 'ready') window.stAddonUsage?.track(root, 'engraving_preview_used', {addon: 'engraving'}, true);
    const el = q(root, '[data-eng-preview-status]');
    if (el) { el.textContent = text || ''; el.dataset.tone = status; }
  }

  function invalidateConsent(root) {
    const c = q(root, '[data-eng-consent]');
    if (c) {
      const wasApproved = c.checked || c.dataset.approved === 'true';
      c.dataset.approved = 'false';
      c.checked = false;
      c.defaultChecked = false;
      c.removeAttribute('checked');
      const note = q(root, '[data-eng-consent-note]');
      if (note && wasApproved) note.hidden = false;
      // Theme variant changes can restore default form state in the same event.
      // Clear that state after their synchronous handlers, without accepting a
      // restored checkbox as a fresh customer approval.
      queueMicrotask(() => {
        if (c.dataset.approved !== 'true') c.checked = false;
      });
    }
  }

  // Threshold the rasterized page into black/transparent and trim to the artwork bounds.
  function buildMask(st) {
    const src = st.raster; if (!src) return null;
    const {width: W, height: H} = src;
    const data = src.getContext('2d', {willReadFrequently: true}).getImageData(0, 0, W, H);
    const d = data.data, t = st.threshold;
    let x0 = W, y0 = H, x1 = -1, y1 = -1;
    for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
      const i = (y * W + x) * 4, a = d[i + 3] / 255;
      const lum = (0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2]) * a + 255 * (1 - a);
      if (lum < t) {
        d[i] = d[i + 1] = d[i + 2] = 0; d[i + 3] = 255;
        if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
      } else d[i + 3] = 0;
    }
    if (x1 < 0) return null;
    const full = document.createElement('canvas'); full.width = W; full.height = H;
    full.getContext('2d').putImageData(data, 0, 0);
    const m = document.createElement('canvas'); m.width = x1 - x0 + 1; m.height = y1 - y0 + 1;
    m.getContext('2d').drawImage(full, x0, y0, m.width, m.height, 0, 0, m.width, m.height);
    return m;
  }

  function draw(root) {
    const st = states.get(root); if (!st || !root.isConnected) return;
    try {
    const canvas = q(root, '[data-eng-canvas]');
    const {maxW, maxH} = profile(root);
    const {w, h, ok} = dims(root);
    const logo = kind(root) === 'logo';
    q(root, '[data-eng-logo-controls]').hidden = !logo || !st.raster;
    q(root, '[data-eng-text-controls]').hidden = logo;
    if (!ok) {
      setStatus(root, st, 'error', `Size must be at least 0.1 in, up to ${maxW} in long and ${maxH} in tall.`);
      return;
    }
    const W = Math.round(w * DPI), H = Math.round(h * DPI);
    if (canvas.width !== W) canvas.width = W;
    if (canvas.height !== H) canvas.height = H;
    canvas.style.aspectRatio = `${W} / ${H}`;
    const ctx = canvas.getContext('2d', {willReadFrequently: true});
    ctx.clearRect(0, 0, W, H);
    q(root, '[data-eng-dims]').textContent = `${w}" W x ${h}" H at ${DPI} dpi`;

    if (!logo) {
      const text = (q(root, '[data-eng-company]')?.value || '').trim();
      if (!text) { setStatus(root, st, 'blank', 'Type your company name to see the engraving preview.'); return; }
      const fontId = q(root, '[data-eng-font]')?.value;
      const font = window.stEngravingFontLibrary?.[fontId]?.canvas || FONTS[fontId] || FONTS.sans;
      let size = H * 0.62;
      ctx.font = font.replace('{s}', size);
      const mw = ctx.measureText(text).width;
      if (mw > W * 0.94) size = size * (W * 0.94) / mw;
      ctx.font = font.replace('{s}', size);
      ctx.fillStyle = '#141414'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(text, W / 2, H / 2);
      setStatus(root, st, 'ready', 'Preview of black laser text. Actual size and placement are confirmed by our team.');
      return;
    }
    if (st.status === 'loading') return;
    if (!st.mask) {
      if (st.status !== 'error' && st.status !== 'blank') setStatus(root, st, 'idle', 'Upload your artwork to preview it in black.');
      return;
    }
    const m = st.mask;
    const base = st.mode === 'fill' ? Math.max(W / m.width, H / m.height) : Math.min(W / m.width, H / m.height);
    const s = base * st.zoom;
    const dw = m.width * s, dh = m.height * s;
    const ox = (W - dw) / 2 + (st.x / 100) * (Math.abs(W - dw) / 2 + W / 4);
    const oy = (H - dh) / 2 + (st.y / 100) * (Math.abs(H - dh) / 2 + H / 4);
    ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(m, ox, oy, dw, dh);
    // Re-binarize so the exported PNG is strictly black or transparent.
    const img = ctx.getImageData(0, 0, W, H), d = img.data;
    let ink = 0;
    for (let i = 0; i < d.length; i += 4) {
      if (d[i + 3] >= 128) { d[i] = d[i + 1] = d[i + 2] = 0; d[i + 3] = 255; ink++; } else d[i + 3] = 0;
    }
    ctx.putImageData(img, 0, 0);
    if (!ink) setStatus(root, st, 'blank', 'The logo is outside the engraving area. Use Fit or Reset.');
    else setStatus(root, st, 'ready', 'Black single-color preview. Layout only: fit, fill, crop and position. The original file is kept for production.');
    } finally {
      const {size} = profile(root), {w, h, ok} = dims(root);
      const locations = window.stEngravingLocations?.profile(root);
      window.stSheavePreview?.render(root, q(root, '[data-eng-canvas]'), {
        size: locations?.frameSides && !locations.wheelSides ? '' : size, widthIn: w, heightIn: h, ready: ok && st.status === 'ready',
        marks: locations?.marksPerFace || 1, back: locations?.wheelSides === 2
      });
      window.stEngravingPackages?.renderDiagram(root, q(root, '[data-eng-canvas]'), {ready: ok && st.status === 'ready', widthIn: w});
      window.stFramePreview?.render(root, q(root, '[data-eng-canvas]'), {
        size: locations?.frameSides ? size : '', ready: ok && st.status === 'ready',
        combined: !!(locations?.frameSides && locations?.wheelSides)
      });
      // Combined selections have one photo with both marks, not a wheel-only
      // photo followed by a second frame photo further down the page.
      const wheelCard = q(root, '[data-st-sheave-preview]');
      if (wheelCard && locations?.frameSides) wheelCard.hidden = true;
    }
  }

  function schedule(root) {
    const st = states.get(root); if (!st || st.raf) return;
    st.raf = requestAnimationFrame(() => { st.raf = 0; draw(root); });
  }

  async function renderPage(root, st, token) {
    st.raster = st.mask = null;
    setStatus(root, st, 'loading', `Rendering page ${st.page}…`);
    try {
      const page = await st.doc.getPage(st.page);
      if (token !== st.token || !root.isConnected) return;
      const v1 = page.getViewport({scale: 1});
      const scale = Math.min(6, 2400 / Math.max(v1.width, v1.height));
      const vp = page.getViewport({scale});
      const c = document.createElement('canvas');
      c.width = Math.ceil(vp.width); c.height = Math.ceil(vp.height);
      await page.render({canvasContext: c.getContext('2d', {willReadFrequently: true}), viewport: vp}).promise;
      if (token !== st.token || !root.isConnected) return;
      st.raster = c;
      st.mask = buildMask(st);
      if (!st.mask) { setStatus(root, st, 'blank', 'This page has no dark artwork at the current threshold. Pick another page or raise the threshold.'); draw(root); return; }
      st.status = 'rendered';
      draw(root);
    } catch (e) {
      if (token !== st.token) return;
      st.mask = null;
      setStatus(root, st, 'error', `Could not render this PDF page: ${e?.message || 'unknown error'}.`);
      draw(root);
    }
  }

  async function onFile(root) {
    const st = states.get(root);
    const file = q(root, '[data-eng-file]')?.files[0];
    const token = ++st.token;
    if (st.doc) { try { st.doc.destroy(); } catch (e) { /* ignore */ } st.doc = null; }
    st.raster = st.mask = null;
    resetLayout(root, st, false);
    const pageSel = q(root, '[data-eng-page]');
    pageSel.innerHTML = '';
    q(root, '[data-eng-page-wrap]').hidden = true;
    if (!file) { setStatus(root, st, 'idle', 'Upload artwork to preview your logo in black.'); draw(root); return; }
    setStatus(root, st, 'loading', 'Loading artwork preview…');
    draw(root);
    try {
      if (!window.stEngravingFiles) throw Error('Artwork support is still loading. Refresh and try again.');
      const info = await window.stEngravingFiles.validate(file);
      if (token !== st.token || !root.isConnected) return;
      st.sourceType = info.ext;
      if (info.kind !== 'pdf') {
        const raster = await window.stEngravingFiles.rasterize(file, info);
        if (token !== st.token || !root.isConnected) return;
        st.raster = raster;
        st.mask = buildMask(st);
        st.status = st.mask ? 'rendered' : 'blank';
        if (!st.mask) setStatus(root, st, 'blank', 'No dark artwork at this threshold. Raise the threshold or upload a darker image.');
        draw(root);
        return;
      }
      const [lib, buf] = await Promise.all([loadPdfjs(), file.arrayBuffer()]);
      if (token !== st.token || !root.isConnected) return;
      const doc = await lib.getDocument({data: new Uint8Array(buf), isEvalSupported: false, enableXfa: false}).promise;
      if (token !== st.token || !root.isConnected) { doc.destroy(); return; }
      st.doc = doc; st.page = 1;
      for (let i = 1; i <= doc.numPages; i++) pageSel.add(new Option(`Page ${i}`, String(i)));
      q(root, '[data-eng-page-wrap]').hidden = doc.numPages < 2;
      await renderPage(root, st, token);
    } catch (e) {
      if (token !== st.token) return;
      setStatus(root, st, 'error', e?.name === 'PasswordException'
        ? 'This PDF is password protected. Export an unprotected PDF and upload it again.'
        : `Artwork preview failed: ${e?.message || 'unknown error'}.`);
      draw(root);
    }
  }

  function resetLayout(root, st, redraw = true) {
    st.mode = 'fit'; st.zoom = 1; st.x = 0; st.y = 0;
    q(root, '[data-eng-zoom]').value = '100';
    q(root, '[data-eng-x]').value = '0';
    q(root, '[data-eng-y]').value = '0';
    syncModeButtons(root, st);
    if (redraw) schedule(root);
  }
  function syncModeButtons(root, st) {
    root.querySelectorAll('[data-eng-mode]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.engMode === st.mode)));
  }

  function init(root) {
    if (!root || states.has(root) || !q(root, '[data-eng-preview]')) return;
    const st = {status: 'idle', token: 0, doc: null, raster: null, mask: null, page: 1, threshold: 160, mode: 'fit', zoom: 1, x: 0, y: 0, raf: 0};
    states.set(root, st);
    syncLimits(root);
    root.addEventListener('change', e => {
      const t = e.target;
      if (t.matches('[data-eng-sheave]')) {
        invalidateConsent(root); syncLimits(root, true); resetLayout(root, st); return;
      }
      if (t.matches('[data-eng-consent]')) {
        t.dataset.approved = String(t.checked);
        const n = q(root, '[data-eng-consent-note]');
        if (n && t.checked) n.hidden = true;
        return;
      }
      if (t.matches('[data-eng-file]')) { invalidateConsent(root); onFile(root); return; }
      if (t.matches('[data-eng-page]') && st.doc) {
        invalidateConsent(root); st.page = Number(t.value) || 1; resetLayout(root, st, false); renderPage(root, st, ++st.token).catch(e => setStatus(root, st, 'error', e.message)); return;
      }
      if (t.matches('[data-eng-w],[data-eng-h]')) {
        const {maxW, maxH} = profile(root);
        const max = t.matches('[data-eng-w]') ? maxW : maxH;
        const v = Number(t.value);
        if (!(v >= 0.1)) t.value = '0.1'; else if (v > max) t.value = String(max);
        invalidateConsent(root); schedule(root);
      }
      if (t.matches('[data-eng-option],[data-eng-placement],[data-eng-font]')) { invalidateConsent(root); syncLimits(root); schedule(root); }
    });
    root.addEventListener('input', e => {
      const t = e.target;
      if (t.closest('[data-eng-preview]') || t.matches('[data-eng-company]')) invalidateConsent(root);
      if (t.matches('[data-eng-zoom]')) st.zoom = Math.max(0.25, Number(t.value) / 100);
      else if (t.matches('[data-eng-x]')) st.x = Number(t.value);
      else if (t.matches('[data-eng-y]')) st.y = Number(t.value);
      else if (t.matches('[data-eng-threshold]')) {
        st.threshold = Number(t.value);
        clearTimeout(st.tTimer);
        st.tTimer = setTimeout(() => {
          if (!st.raster || !root.isConnected) return;
          st.mask = buildMask(st);
          if (!st.mask) setStatus(root, st, 'blank', 'No dark artwork at this threshold. Raise the threshold.');
          else st.status = 'rendered';
          draw(root);
        }, 120);
        return;
      }
      schedule(root);
    });
    root.addEventListener('click', e => {
      const b = e.target.closest('[data-eng-mode],[data-eng-reset]');
      if (!b || !root.contains(b)) return;
      e.preventDefault();
      invalidateConsent(root);
      if (b.matches('[data-eng-reset]')) {
        syncLimits(root, true);
        q(root, '[data-eng-threshold]').value = '160';
        if (st.threshold !== 160 && st.raster) { st.threshold = 160; st.mask = buildMask(st); }
        st.threshold = 160;
        resetLayout(root, st);
      } else {
        st.mode = b.dataset.engMode; st.zoom = 1; st.x = 0; st.y = 0;
        q(root, '[data-eng-zoom]').value = '100'; q(root, '[data-eng-x]').value = '0'; q(root, '[data-eng-y]').value = '0';
        syncModeButtons(root, st); schedule(root);
      }
    });
    syncModeButtons(root, st);
    if (q(root, '[data-eng-file]')?.files[0]) onFile(root); else draw(root);
  }

  async function exportPreview(root) {
    init(root);
    const st = states.get(root);
    if (!st) throw Error('The engraving preview is not available. Refresh the page and try again.');
    // Export the latest threshold, even when Add is clicked before the debounce.
    if (st.tTimer) {
      clearTimeout(st.tTimer);
      st.tTimer = null;
      if (st.raster) st.mask = buildMask(st);
    }
    if (st.raf) { cancelAnimationFrame(st.raf); st.raf = 0; }
    draw(root);
    const logo = kind(root) === 'logo';
    const {size, maxW, maxH} = profile(root);
    const locations = window.stEngravingLocations?.profile(root);
    if (!locations?.valid) {
      throw Error('Choose an available engraving package for this tool. Feeding sheaves allow one frame side only.');
    }
    const {w, h, ok} = dims(root);
    if (!ok) throw Error(`Set an engraving size up to ${maxW} in long and ${maxH} in tall.`);
    if (st.status === 'loading') throw Error('Your artwork preview is still loading. Wait for it to finish, then try again.');
    if (st.status === 'error') throw Error(q(root, '[data-eng-preview-status]')?.textContent || 'The artwork preview failed.');
    if (st.status !== 'ready') throw Error(logo ? 'The logo preview is blank. Upload artwork with dark content and adjust the layout.' : 'Enter the company name exactly as you want it engraved.');
    const canvas = q(root, '[data-eng-canvas]');
    const blob = await new Promise((res, rej) => canvas.toBlob(b => b ? res(b) : rej(Error('Could not create the preview image.')), 'image/png'));
    const file = new File([blob], `engraving-preview-${w}x${h}in.png`, {type: 'image/png'});
    const settings = {
      v: 3, kind: logo ? 'logo' : 'text', sourceType: logo ? st.sourceType : 'typed-text', sheaveSize: size || null, maxWidthIn: maxW, maxHeightIn: maxH, widthIn: w, heightIn: h, dpi: DPI, px: [canvas.width, canvas.height],
      color: 'black-single-color',
      ...(logo ? {page: st.page, mode: st.mode, zoom: +st.zoom.toFixed(3), panX: st.x, panY: st.y, threshold: st.threshold, pdfjs: PDFJS_VERSION}
               : {font: q(root, '[data-eng-font]')?.value || 'sans', fontLabel: window.stEngravingPackages?.fontLabel(q(root, '[data-eng-font]')?.value)}),
      package: locations.package, marksPerFace: locations.marksPerFace, totalMarks: locations.totalMarks,
      wheelSides: locations.wheelSides, frameSides: locations.frameSides, wheelAnglesDeg: locations.wheelAngles
    };
    const wantsWheel = size && (!locations || locations.wheelSides > 0);
    if (wantsWheel && !window.stSheavePreview?.export) throw Error('The sheave placement preview is still loading. Refresh and try again.');
    const mockup = wantsWheel ? await window.stSheavePreview.export(root) : null;
    if (wantsWheel && !mockup?.file) throw Error('The wheel placement preview could not be saved. Try again.');
    if (locations?.frameSides && !window.stFramePreview?.export) throw Error('The frame preview is still loading. Refresh and try again.');
    const frameMockup = locations?.frameSides ? await window.stFramePreview.export(root) : null;
    if (locations?.frameSides && !frameMockup?.file) throw Error('The frame placement preview could not be saved. Try again.');
    const diagram = await window.stEngravingPackages.exportDiagram(root);
    if (mockup) settings.placement = mockup.placement;
    if (frameMockup) settings.framePlacement = frameMockup.placement;
    if (locations?.size) settings.locations = locations;
    return {file, mockup, frameMockup, diagram, locations, sheaveSize: size, settings: JSON.stringify(settings),
      size: locations?.frameSides && !locations.wheelSides ? (['feed-4', 'feed-5', 'feed-6'].includes(size)
        ? `${w} in wide x ${h} in high on feeding-sheave frame (max ${maxW} x ${maxH} in)`
        : 'Frame artwork fitted to selected surface; final dimensions confirmed before engraving')
        : `${w} in long x ${h} in tall per wheel mark (max ${maxW} x ${maxH} in on wheel)${frameMockup ? '; frame artwork fitted separately to arm, frame size to be confirmed' : ''}`};
  }

  window.stEngravingPreview = {init, export: exportPreview, version: 1};
  const boot = () => document.querySelectorAll('[data-st-engraving]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, {once: true}); else boot();
})();
